/**
 * EvidenceSignals — compact at-a-glance intelligence signal strip.
 *
 * Renders three small signal indicators in a row inside DocRow,
 * communicating the AI readiness of a document's evidence package:
 *
 *   ● Text     — structured text was extracted (row.parsed === true)
 *   ● Doctype  — AI identified source document type
 *   ● Tags     — metadata tags have been extracted / populated
 *
 * Each signal is a 6×6 filled circle (active) or outlined circle
 * (inactive). Colour follows evidence quality:
 *   active  → aiColors.evidenceHigh (emerald) or evidenceMid (amber)
 *             for partial states
 *   inactive → surfaceColors.lineStrong (gray outline)
 *
 * Signals only render when source_classification is present —
 * rows still in uploading/indexing/classifying state show no strip.
 * The parent DocRow guards the render:
 *   {row.source_classification && <EvidenceSignals row={row} />}
 *
 * Uses `title` attributes for tooltip text; no additional DOM
 * elements required.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { surfaceColors } from "../tokens";
import { aiColors } from "../tokens";
import type { DocumentRow } from "../types";

export interface EvidenceSignalsProps {
  row: DocumentRow;
}

interface Signal {
  key: string;
  title: string;
  active: boolean;
  color: string; // filled color when active
}

function buildSignals(row: DocumentRow): Signal[] {
  const sc = row.source_classification;
  const tags = sc?.tags ?? {};
  const tagTotal = Object.keys(tags).length;
  const tagFilled = Object.values(tags).filter((v) => v !== null).length;

  return [
    {
      key: "parsed",
      title: row.parsed === true ? "Text extracted — full coverage" : "Text not extracted — limited coverage",
      active: row.parsed === true,
      color: aiColors.evidenceHigh,
    },
    {
      key: "doctyped",
      title: sc?.source_doctype ? `Doctype identified: ${sc.source_doctype}` : "Doctype not yet identified",
      active: !!sc?.source_doctype,
      color: aiColors.evidenceHigh,
    },
    {
      key: "tagged",
      title: tagTotal === 0 ? "No metadata tags" : `${tagFilled}/${tagTotal} metadata tags populated`,
      active: tagTotal > 0 && tagFilled > 0,
      // Partial fill → amber; all filled → green
      color: tagFilled > 0 && tagFilled < tagTotal ? aiColors.evidenceMid : aiColors.evidenceHigh,
    },
  ];
}

const useStyles = makeStyles({
  root: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    marginLeft: "auto",
    flexShrink: 0,
  },
  dot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
    border: "1px solid transparent",
    display: "inline-block",
  },
  dotInactive: {
    backgroundColor: "transparent",
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
});

export const EvidenceSignals: React.FC<EvidenceSignalsProps> = ({ row }) => {
  const styles = useStyles();
  const signals = buildSignals(row);

  return (
    <span className={styles.root} aria-label="Evidence signals">
      {signals.map((sig) => (
        <span
          key={sig.key}
          className={`${styles.dot} ${sig.active ? "" : styles.dotInactive}`}
          style={sig.active ? { backgroundColor: sig.color, borderColor: sig.color } : undefined}
          title={sig.title}
          aria-hidden="true"
        />
      ))}
    </span>
  );
};

export default EvidenceSignals;
